import { Download } from 'lucide-react';
import { useMemo } from 'react';

import { Button } from '@/components/ui/button';
import type { ReportCourse } from '@/hooks/queries/useReportCourses';
import { useTabLabels } from '@/hooks/useTabLabels';

import CourseLink from './CourseLink';
import ReportTable, { type ReportColumn } from './ReportTable';
import { buildOutcomesRows, downloadCsv, reportCsvFilename } from './reportUtils';

type OutcomesReportProps = {
	courses: ReportCourse[];
	schoolId: string;
};

const OutcomesReport = ({ courses, schoolId }: OutcomesReportProps) => {
	const labels = useTabLabels();
	const rows = buildOutcomesRows(courses);
	const outcomeLabel = labels.outcomes;

	const columns: ReportColumn[] = useMemo(
		() => [
			{ id: 'grade', label: 'Grade', sortType: 'grade' },
			{ id: 'discipline', label: 'Discipline' },
			{ id: 'course', label: 'Course' },
			{ id: 'outcome', label: outcomeLabel },
		],
		[outcomeLabel]
	);

	const handleDownload = () => {
		downloadCsv(
			reportCsvFilename('outcomes', schoolId),
			['Grade', 'Discipline', 'Course', outcomeLabel],
			rows.map((row) => [row.grade, row.discipline, row.course, row.outcomeText])
		);
	};

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<h2 className="text-[16px] font-bold leading-5 text-(--header-text)">
						{outcomeLabel} inventory
					</h2>
					<p className="mt-1 text-sm text-(--header-text)">
						{rows.length} {outcomeLabel.toLowerCase()}
					</p>
				</div>
				<Button type="button" variant="outline" size="small" onClick={handleDownload}>
					<Download size={14} className="mr-1.5" />
					Download CSV
				</Button>
			</div>
			<ReportTable
				columns={columns}
				rows={rows.map((row) => ({
					key: row.outcomeId || `${row.courseId}|${row.outcomeText}`,
					searchText: [row.grade, row.discipline, row.course, row.outcomeText].join(' '),
					sortValues: [row.grade, row.discipline, row.course, row.outcomeText],
					cells: [
						row.grade || '—',
						row.discipline || '—',
						<CourseLink key="course" courseId={row.courseId}>
							{row.course}
						</CourseLink>,
						row.outcomeText || '—',
					],
				}))}
				emptyMessage={`No ${outcomeLabel.toLowerCase()} found for the current filters.`}
				searchPlaceholder={`Search ${outcomeLabel.toLowerCase()}…`}
			/>
		</div>
	);
};

export default OutcomesReport;
